import { useUserStatus } from '@btcdirect/composables';
import { dispatch } from '@btcdirect/functions';
import { ApiCommunicationStates } from '@btcdirect/shared/src/composables/api/types';
import { useUserInfo } from '@btcdirect/shared/src/composables/api/user/info';
import { useUserLogin } from '@btcdirect/shared/src/composables/api/user/login';
import { useRegisterIdentifier } from '@btcdirect/shared/src/composables/api/user/register-identifier';
import { useConfiguration } from '@btcdirect/shared/src/composables/configuration';
import { useInterval } from '@btcdirect/shared/src/composables/util/interval';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { DispatchEvents } from '@btcdirect/shared/src/types';
import { useAuthenticationStore, useConfigurationStore, useLimitsStore, useUserInfoStore, useWalletAddressesStore } from '@btcdirect/stores';
import { computed, reactive, readonly } from 'vue';

const { debug } = useLogger();
const { stop } = useInterval();

interface UserAuthentication {
    uuid: string; // TODO: Check if we need this for anything as it is not being used anywhere
    communication: ApiCommunicationStates;
}

// TODO: Move uuid to a user-store and set communication as separate ref
const userCommunication = reactive<UserAuthentication>({
    uuid: null,
    communication: ApiCommunicationStates.NOT_STARTED
});

export const useUserAuthentication = () => {
    const authenticationStore = useAuthenticationStore();
    const userInfoStore = useUserInfoStore();
    const walletAddressesStore = useWalletAddressesStore();
    const limitsStore = useLimitsStore();
    const { resetCommunication: resetUserStatusCommunication } = useUserStatus();
    const configurationStore = useConfigurationStore();

    // @ts-ignore
    const apiCommunicationStatesKeys = Object.keys(ApiCommunicationStates).filter((v) => isNaN(Number(v)));

    const logout = (communicationState: ApiCommunicationStates = ApiCommunicationStates.NOT_STARTED): void => {
        if (!authenticationStore.user.authenticated) {
            debug('Logging out unauthenticated user.', `CommunicationState: ${apiCommunicationStatesKeys[communicationState]}.`);
        }

        stop('user-info');

        userInfoStore.setUserInfo(null);

        authenticationStore.setUserAuthentication(false);
        authenticationStore.setUserTokens({ authorization: null, refresh: null, identifier: null });
        authenticationStore.setOneTimeAuthentication(null, null);

        limitsStore.setUserLimits([]);
        limitsStore.setCardLimits([]);

        if (communicationState !== ApiCommunicationStates.NOT_STARTED && !walletAddressesStore.addressesAreManuallyProvided) {
            walletAddressesStore.setWalletAddresses([]);
        }

        userCommunication.uuid = null;
        userCommunication.communication = communicationState;

        resetUserStatusCommunication();

        if (!communicationState) {
            dispatch(DispatchEvents.SIGNED_OUT);
        }
    };

    /**
     * Authenticate the user with username and password.
     * @param username
     * @param password
     */
    const authenticateWithCredentials = async (username: string, password: string): Promise<void> => {
        userCommunication.communication = ApiCommunicationStates.COMMUNICATING;

        try {
            const result = await useUserLogin().login(username, password);
            userCommunication.uuid = result.uuid;

            authenticationStore.setUserAuthentication(!!result.uuid);
            authenticationStore.setUserTokens({ authorization: result.token, refresh: result.refreshToken });

            await getUserInfo();

            const { enableUserIdentifier } = useConfiguration();
            if (enableUserIdentifier.value) {
                await useRegisterIdentifier().registerIdentifier(false);
            }

            userCommunication.communication = ApiCommunicationStates.SUCCESSFUL;
            dispatch(DispatchEvents.SIGNED_IN);
        } catch (exception) {
            logout(exception && exception.isCancelled ? ApiCommunicationStates.CANCELLED : ApiCommunicationStates.FAILED);

            return !!exception.data?.errors ? Promise.reject(Object.values(exception.data.errors)[0]) : Promise.reject();
        }
    };

    /**
     * Try to authenticate automatically.
     * If there's an identifier available, it will be used. If not, the refresh token will be tried.
     * Note that the identifier is less secure and should only be used inside trusted partner applications.
     */
    const autoAuthenticate = async (): Promise<void> => {
        if (authenticationStore.user.authenticated) {
            return;
        }

        if (!authenticationStore.user.tokens.identifier && !authenticationStore.user.tokens.authorization && !authenticationStore.oneTimeAuthentication.token) {
            logout(ApiCommunicationStates.NOT_STARTED);
            return;
        }

        userCommunication.communication = ApiCommunicationStates.COMMUNICATING;

        try {
            if (!!authenticationStore.oneTimeAuthentication.token) {
                const result = await useUserLogin().oneTimeLogin(
                    authenticationStore.oneTimeAuthentication.token,
                    authenticationStore.oneTimeAuthentication.uuid
                );

                userCommunication.uuid = result.uuid;

                authenticationStore.setUserAuthentication(!!result.uuid);
                authenticationStore.setUserTokens({ authorization: result.token, refresh: result.refreshToken });

                // Unset the one-time authentication token as it's been used
                authenticationStore.setOneTimeAuthentication(null, null);
            }

            await getUserInfo();

            userCommunication.communication = ApiCommunicationStates.SUCCESSFUL;
            dispatch(DispatchEvents.SIGNED_IN);
        } catch (exception) {
            logout(exception && exception.isCancelled ? ApiCommunicationStates.CANCELLED : ApiCommunicationStates.FAILED);
        }
    };

    /**
     * Get the user information.
     * If successful, the user is authenticated properly.
     */
    async function getUserInfo(): Promise<void> {
        try {
            await useUserInfo().retrieveUserInfo();
            authenticationStore.setUserAuthentication(!!userInfoStore.user?.emailAddress);
        } catch (exception) {
            authenticationStore.setUserAuthentication(false);
            return Promise.reject(exception);
        }
    }

    return {
        user: readonly(userCommunication),
        communicating: computed<boolean>(() => userCommunication.communication === ApiCommunicationStates.COMMUNICATING),
        authenticateWithCredentials,
        autoAuthenticate,
        logout
    };
};
